import fecha from 'fecha';
import locales, { TranslationType, humanized, remaining } from './locales';
import getCurrentLanguageKey from '../getCurrentLanguageKey';

export const FULL_FORMAT = 'YYYY-MM-DDTHH:mm:ssZZ';
// 2017-08-21T22:09:55.617047+00:00
const CUSTOM = 'MMM D, YYYY h:mm:ss a';

/**
 * Validate if 2 date objects correspond to the same day.
 *
 * @param {Date} date1 - first Date object to be compared
 * @param {Date} date2 - second Date object to be compared
 */
export const isSameDay = (date1: Date, date2: Date) =>
  date1.getDate() === date2.getDate() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getFullYear() === date2.getFullYear();

/**
 * Show duration in seconds as Xm Xs / Xh Xm / Xd Xh
 *
 * @param {number} durationInSeconds - The duration to in seconds
 *
 * @example
 * console.log(toMinutes(244)); // 4m 4s
 * console.log(toMinutes(4200)); // 1h 10m
 * console.log(toMinutes(90000)); // 1d 1h
 */
export const toMinutes = (durationInSeconds: number) => {
  const minutes = Math.floor(durationInSeconds / 60);

  if (minutes < 60) {
    const remainingSeconds = Math.ceil(durationInSeconds - minutes * 60);

    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    const remainingMinutes = Math.ceil(minutes - hours * 60);

    return `${hours}h ${remainingMinutes}m`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.ceil(hours - days * 24);

  return `${days}d ${remainingHours}h`;
};

/**
 * Show time in seconds as mm:ss or hh:mm:ss
 *
 * @param {number} timeInSeconds - The time in seconds
 * @param {boolean} ignoreHours - If true, timestamp will always be mm:ss even for great numbers
 *
 * @example
 * console.log(toMinutes(244)); // 04:04
 * console.log(toMinutes(7800)); // 02:10:00
 * console.log(toMinutes(7800, true)); // 130:00
 */
export const toTimestamp = (timeInSeconds: number, ignoreHours?: boolean) => {
  let minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.ceil(timeInSeconds - minutes * 60);

  if (!ignoreHours && minutes >= 60) {
    const hours = Math.floor(minutes / 60);

    minutes = Math.ceil(minutes - hours * 60);
    return `${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(
      -2,
    )}:${`0${seconds}`.slice(-2)}`;
  }
  return `${
    ignoreHours && minutes >= 10 ? minutes : `0${minutes}`.slice(-2)
  }:${`0${seconds}`.slice(-2)}`;
};

export const toDate = (str: string, fmt = FULL_FORMAT, lang?: 'es' | 'pt') => {
  fecha.i18n = locales[lang || getCurrentLanguageKey()];

  return fecha.parse(str, fmt);
};

export const toString = (date: Date, fmt?: string, lang?: 'es' | 'pt') => {
  fecha.i18n = locales[lang || getCurrentLanguageKey()];
  const d = fecha.format(date, fmt || CUSTOM);

  return d
    .charAt(0)
    .toUpperCase()
    .concat(d.slice(1));
};

/**
 * Receives a string or Date object and returns it in the given format.
 *
 * @param {string | Date} dateToFormat - Date object or date as string
 * @param {string} fmt - Format expected
 * @param {inputFmt} fmt - If you provide a string, you can specify the input format, otherwise it will be assumed to have the FULL_FORMAT
 * @param {'es' | 'pt'} fmt - You can force the target language. By default, it will use the current language key
 *
 * @example
 * const fullDate = '2017-12-21T22:09:55.617047+00:00';
 * const dateWithCustomFormat = '2017/12/21';
 *
 * console.log(format(fullDate, 'MMM D, YYYY')); // Dic 21, 2017
 * console.log(format(sateWithCustomFormat, 'MMM D, YYYY', 'YYYY/MM/DD')); // Dic 21, 2017
 */
export const format = (
  dateToFormat: string | Date,
  fmt?: string,
  inputFmt?: string,
  lang?: 'es' | 'pt',
) => {
  const date =
    typeof dateToFormat === 'string'
      ? (toDate(dateToFormat, inputFmt) as Date)
      : dateToFormat;

  if (date && typeof date !== 'boolean') {
    return toString(date, fmt || CUSTOM, lang);
  }
};

/**
 * Compares 2 dates and returns the greater one.
 * You can provide the date object itself or a date string (FULL_FORMAT)
 */
export const getGreaterDate = (
  date1Param?: Date | string | null,
  date2Param?: Date | string | null,
) => {
  const date1 =
    typeof date1Param === 'string' ? (toDate(date1Param) as Date) : date1Param;
  const date2 =
    typeof date2Param === 'string' ? (toDate(date2Param) as Date) : date2Param;

  if (!date1 && !date2) {
    return undefined;
  }
  if (!date1) {
    return date2;
  }
  if (!date2) {
    return date1;
  }
  return date1.getTime() > date2.getTime() ? date1 : date2;
};

const calculateTimeDifference = (
  date: string | Date,
  translation: TranslationType,
) => {
  const now = new Date();
  const then = typeof date === 'string' ? (toDate(date) as Date) : date;
  const millis = Math.abs(now.getTime() - then.getTime());

  if (millis < 1000 * 60) {
    return translation.SECONDS_AGO;
  }
  const mins = Math.ceil(millis / (1000 * 60));

  if (mins === 1) {
    return translation.A_MINUTE_AGO;
  }
  if (mins < 60) {
    return translation.MINUTES_AGO(mins);
  }
  const hours = Math.ceil(mins / 60);

  if (hours === 1) {
    return translation.AN_HOUR_AGO;
  }
  if (hours < 24) {
    return translation.HOURS_AGO(hours);
  }
  const days = Math.ceil(hours / 24);

  if (days === 1) {
    return translation.A_DAY_AGO;
  }
  if (days < 30) {
    return translation.DAYS_AGO(days);
  }
  const months = Math.ceil(days / 30);

  if (months === 1) {
    return translation.A_MONTH_AGO;
  }
  if (months < 12) {
    return translation.MONTHS_AGO(months);
  }
  const years = Math.ceil(months / 12);

  if (years === 1) {
    return translation.A_YEAR_AGO;
  }
  return translation.YEARS_AGO(years);
};

export const humanize = (date: string | Date, lang?: 'es' | 'pt') => {
  const langKey = lang || getCurrentLanguageKey();

  return calculateTimeDifference(date, humanized[langKey]);
};

export const remainingTime = (date: string | Date, lang?: 'es' | 'pt') => {
  const langKey = lang || getCurrentLanguageKey();

  return calculateTimeDifference(date, remaining[langKey]);
};

const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];
const THIS_MONTH = new Date().getMonth();

export const getMonthName = (month = THIS_MONTH) => MONTHS[month];
